import config from 'config';

import { cioFacetsToUrlFilters } from '~/grid/utils/cioUtils';
import { plusSizes } from '~/shared/constants/productSizes';
import { getIsPlusProfile } from '~/shared/utils/getIsPlusProfile';

import { GRID_TYPES } from '../hooks/useGridInputs';
import { UnifiedProduct } from '../types/product';
import { Swatch } from '../types/relatedProduct';

import { isCioColorFacet, isolateSizeFacets } from './filters';
import {
  buildImageServiceUrlFromItemNumber,
  buildSwatchImageUrlFromSku,
} from './images';

const useSavagePlusSizeFallback = config.get(
  'public.brand.features.useSavagePlusSizeFallback'
);

export type ActiveProduct = UnifiedProduct & {
  pdpLink: string;
  swatchLabel: string;
};

/**
 * Generates a psrc value based on the provided psrc and grid type.
 * Adds special handling for Builder Constructor grids to deduplicate the '-cio' suffix
 * or add it if missing.
 *
 * @param {Object} param - The parameters for generating the psrc.
 * @param {string} param.psrc - The original psrc value.
 * @param {string} param.gridType - The type of grid the product is from.
 * @returns {string|undefined} - The generated psrc value or undefined if not provided.
 */
export const getPSRC = ({
  psrc,
  gridType,
}: {
  psrc: string | undefined;
  gridType?: string;
}): string | undefined => {
  let suffix = '';
  if (gridType === GRID_TYPES.BUILDER_CONSTRUCTOR_GRID) {
    suffix = '-cio';
  }
  return `${psrc?.replace(suffix, '')}${suffix}`;
};

/**
 * Generates a product detail page link based on the permalink and psrc.
 * Adds special handling for Builder Constructor grids to deduplicate the '-cio' suffix in the psrc
 * or add it if missing.
 *
 * @param {Object} param - The parameters for generating the link.
 * @param {string} param.permalink - The permalink of the product.
 * @param {string} param.psrc - The psrc of the product.
 * @param {string} param.gridType - The type of grid the product is from.
 * @returns {string} - The generated product detail page link.
 */
const getPDPlink = ({
  permalink,
  psrc,
  gridType,
}: {
  permalink: string;
  psrc?: string;
  gridType?: string;
}): string => {
  if (!psrc) {
    return `/products/${permalink}`;
  }

  const finalPsrc = getPSRC({ psrc, gridType });
  return `/products/${permalink}?psrc=${finalPsrc}`;
};

/**
 * Data from Cio does not have a swatch label, so we need to get it from the color facet.
 * The assumption is that the first color facet option is the one we want to use and
 * matches the swatch label from related products.
 *
 * This will be used as a fallback in the event that the color is not found within the
 * `alias` field where the color is included between parentheses.
 *
 * @param {Array} facets - The list of facets from the product data.
 * @returns {string} - The swatch label.
 */
const getSwatchLabelFromColorFacet = (facets: any[]): string => {
  const colorFacet = facets?.find((facet) => isCioColorFacet(facet?.name));

  // The product will typically have one (the first) color facet option associated with itself,
  // due to the nature of our product data, where we associate a separate product id for each color variation
  return colorFacet?.values?.[0];
};

/**
 * Data from Cio does not have a swatch label, so we need to derive it. The alias field
 * contains the color name and is typically formatted as `ITEM-NUMBER (Color Name)`. This
 * function will return the color name between the parentheses.
 *
 * @param {string} alias - The alias of the product.
 * @returns {string} - The color name.
 */
const getColorFromAlias = (alias: string): string => {
  const colorName = alias?.match(/\((.*)\)/)?.[1];

  return colorName;
};

/**
 * Formats the primary product data to match the active product format.
 *
 * @param {Object} param.primaryProductData - The primary product data.
 * @param {string} param.productSource - The source of the product.
 * @param {string} param.gridType - The type of grid the product is from.
 * @returns {ActiveProduct} - The formatted active product object.
 */
const formatPrimaryProductToActiveProduct = ({
  primaryProductData,
  productSource,
  gridType,
}: {
  primaryProductData: any;
  productSource: string;
  gridType?: string;
}): ActiveProduct => {
  return {
    ...primaryProductData,
    pdpLink: getPDPlink({
      permalink: primaryProductData?.permalink,
      psrc: productSource,
      gridType,
    }),
    swatchLabel:
      getColorFromAlias(primaryProductData?.alias) ||
      getSwatchLabelFromColorFacet(primaryProductData?.facets),
  };
};

/**
 * Formats the related product data to match the active product format.
 *
 * IMPORTANT: when we migrate to getRelatedProductsByGroupCode, we should have
 * all the necessary data in the relatedProductData to avoid needing to merge
 * with the primaryProductData as the fallback.
 *
 * @param {Object} param.primaryProductData - The primary product data.
 * @param {Object} param.relatedProductData - The related product data.
 * @param {string} param.productSource - The source of the product.
 * @param {string} param.gridType - The type of grid the product is from.
 * @returns {ActiveProduct} - The formatted active product object.
 */
const formatRelatedProductToActiveProduct = ({
  primaryProductData,
  relatedProductData,
  productSource,
  gridType,
}: {
  primaryProductData: any;
  relatedProductData: any;
  productSource: string;
  gridType?: string;
}): ActiveProduct => {
  return {
    ...primaryProductData,
    ...relatedProductData,
    imageServiceUrl:
      buildImageServiceUrlFromItemNumber(relatedProductData?.itemNumber) ||
      primaryProductData?.imageServiceUrl,
    pdpLink: getPDPlink({
      permalink: relatedProductData?.permalink,
      psrc: productSource,
      gridType,
    }),
    swatchLabel: relatedProductData?.color,
  };
};

/**
 * Get a unified formatted product object between the primary product data and GQL.
 *
 * @param {Object} param.primaryProductData - The primary product data.
 * @param {Array} param.relatedProducts - The list of related products.
 * @param {number} param.activeProductId - The ID of the active product.
 * @param {string} param.productSource - The source of the product.
 * @param {string} param.gridType - The type of grid the product is from.
 * @returns {ActiveProduct} - The formatted product object.
 */
export const getActiveProduct = ({
  primaryProductData,
  relatedProducts,
  activeProductId,
  productSource,
  gridType,
}: {
  primaryProductData: any;
  relatedProducts: any[];
  activeProductId: number;
  productSource: string;
  gridType?: string;
}): ActiveProduct => {
  if (activeProductId === primaryProductData?.id) {
    return formatPrimaryProductToActiveProduct({
      primaryProductData,
      productSource,
      gridType,
    });
  }

  const relatedProductData = relatedProducts?.find(
    (product) => product?.masterProductId === activeProductId
  );

  return formatRelatedProductToActiveProduct({
    primaryProductData,
    relatedProductData,
    productSource,
    gridType,
  });
};

/**
 * Formats the swatch data structure for a primary product.
 *
 * @param {Object} param.primaryProductData - The primary product data.
 * @param {string} param.productSource - The source of the product.
 * @param {Array} param.facets - The list of facets.
 * @param {string} param.gridType - The type of grid the product is from.
 * @returns {Swatch} - The formatted swatch object.
 */
const formatSwatchForPrimaryProduct = ({
  primaryProductData,
  productSource,
  facets,
  gridType,
}: {
  primaryProductData: any;
  productSource: string;
  facets: any[];
  gridType?: string;
}): Swatch => {
  return {
    productId: Number(primaryProductData?.id),
    label:
      getColorFromAlias(primaryProductData?.alias) ||
      getSwatchLabelFromColorFacet(facets),
    url: buildSwatchImageUrlFromSku(
      primaryProductData?.sku,
      primaryProductData?.permalink
    ),
    pdpLink: getPDPlink({
      permalink: primaryProductData?.permalink,
      psrc: productSource,
      gridType,
    }),
    tagIdList:
      primaryProductData?.tagIdList?.map((tag: any) => Number(tag)) || [],
  };
};

/**
 * Formats the swatch data structure for a related product from GQL.
 *
 * @param {Object} param.swatch - The swatch object.
 * @param {string} param.productSource - The source of the product.
 * @param {Object} param.relatedProduct - The related product object.
 * @param {string} param.gridType - The type of grid the product is from.
 * @returns {Swatch} - The formatted swatch object.
 */
const formatSwatchForRelatedProduct = ({
  swatch,
  productSource,
  relatedProduct,
  gridType,
}: {
  swatch: any;
  productSource: string;
  relatedProduct: any;
  gridType?: string;
}): Swatch => {
  return {
    productId: swatch?.productId,
    label: swatch?.label,
    url:
      swatch?.url ||
      buildSwatchImageUrlFromSku(
        relatedProduct?.sku,
        relatedProduct?.permalink
      ),
    pdpLink: getPDPlink({
      permalink: relatedProduct?.permalink,
      psrc: productSource,
      gridType,
    }),
    tagIdList: relatedProduct?.tags?.map((tag: any) => Number(tag?.id)) || [],
  };
};
/**
 * Generates a list of swatches with unified swatch data structure with the primary swatch first.
 * The primary swatch is the one associated with the primary product data.
 * The related product swatches are formatted and filtered to exclude the primary swatch.
 * Related products is not guaranteed to have isSelf swatch
 * we need to add it manually and make sure it's the first one
 *
 * @param {Array} param.swatches - The list of related product swatches.
 * @param {string} param.productSource - The source of the product.
 * @param {Object} param.primaryProductData - The primary product data.
 * @param {Array} param.relatedProducts - The list of related products.
 * @param {string} param.gridType - The type of grid the product is from.
 * @returns {Array} - The list of swatches with the primary swatch first.
 */
export const getSwatchesWithPdpLink = ({
  swatches,
  productSource,
  primaryProductData,
  relatedProducts,
  gridType,
}: {
  swatches: any[];
  productSource: string;
  primaryProductData: any;
  relatedProducts: any[];
  gridType?: string;
}): Swatch[] => {
  const primarySwatch = formatSwatchForPrimaryProduct({
    primaryProductData,
    productSource,
    facets: primaryProductData?.facets,
    gridType,
  });

  if (!swatches?.length || !relatedProducts?.length) {
    return [primarySwatch];
  }

  const relatedProductSwatches = swatches
    ?.map((swatch) => {
      const relatedProduct = relatedProducts?.find(
        (product) =>
          Number(product.masterProductId) === Number(swatch?.productId)
      );

      // Remove swatches that are not available
      if (!relatedProduct?.isAvailable) {
        return null;
      }

      return formatSwatchForRelatedProduct({
        swatch,
        productSource,
        relatedProduct,
        gridType,
      });
    })
    ?.filter(Boolean);

  // filter out swatches that are the same as the primary swatch
  // there are some instances where related products have the same productId as the primary product
  // indicated by the `swatch.isSelf` property, but this is not always the case
  // so we need to filter out the swatches that have the same productId as the primary product
  // and manually build the primary swatch to have a consistent experience
  const filteredRelatedProductSwatches = relatedProductSwatches?.filter(
    (swatch) => swatch?.productId !== primarySwatch?.productId
  );

  // concatenate all swatches
  return [primarySwatch, ...filteredRelatedProductSwatches];
};

/**
 * Products V2-only rules for plus image selection.
 * TODO: this does not account for productFacets like we do for Constructor.
 * We should standardize the logic for all grids in a later ticket:
 * https://techstyle-prod.atlassian.net/browse/FLCE-3225
 *
 * - With a regular profile: any regular selection => regular images; only plus selections => plus images.
 * - With a plus profile: any plus selection => plus images; only regular selections => regular images.
 * - No profile: only plus selections => plus images; otherwise regular images.
 * - No filter selection defaults to profile size.
 *
 * @param {Object} param - The parameters for the function.
 * @param {Object} param.selectedUrlFilters - The selected URL filters.
 * @param {Object} param.profileSizes - The user's profile sizes.
 * @returns {boolean} - True if the product should show a plus size image, false otherwise.
 */
export const shouldShowPlusSizeImageForProductCardForProductsV2 = ({
  selectedUrlFilters,
  profileSizes,
}: {
  selectedUrlFilters: Record<string, any>;
  profileSizes?: any;
}): boolean => {
  // Determine profile type
  const allProfileSizes =
    profileSizes && typeof profileSizes === 'object'
      ? Object.values(profileSizes).filter(Boolean)
      : [];
  const hasProfile = allProfileSizes.length > 0;
  const isPlusProfile = hasProfile
    ? allProfileSizes.some((val) => plusSizes.has(val))
    : undefined;

  // If no filters selected, fallback to profile
  if (!selectedUrlFilters) {
    return !!isPlusProfile;
  }

  const selectedSizes = Object.entries(selectedUrlFilters).flatMap(
    ([filterKey, filterValue]) => {
      if (!filterKey.startsWith('size_') || !Array.isArray(filterValue)) {
        return [];
      }
      return filterValue;
    }
  );

  const hasPlusSizeSelection = selectedSizes.some((val) => plusSizes.has(val));
  const hasRegularSizeSelection = selectedSizes.some(
    (val) => !plusSizes.has(val)
  );

  // If no profile size is setup
  if (!hasProfile) {
    if (hasPlusSizeSelection && !hasRegularSizeSelection) {
      return true; // Only plus sizes selected
    }
    return false; // All other cases show regular
  }

  // If profile size is setup and is regular
  if (hasProfile && !isPlusProfile) {
    if (selectedSizes.length === 0) {
      return false; // No filter selection, use profile (regular)
    }
    if (hasRegularSizeSelection) {
      return false; // As long as a regular size is selected, show regular
    }
    if (hasPlusSizeSelection && !hasRegularSizeSelection) {
      return true; // Only plus sizes selected
    }
    return false;
  }

  // If profile size is setup and is plus
  if (hasProfile && isPlusProfile) {
    if (selectedSizes.length === 0) {
      return true; // No filter selection, use profile (plus)
    }
    if (hasPlusSizeSelection) {
      return true; // As long as a plus size is selected, show plus
    }
    if (!hasPlusSizeSelection && hasRegularSizeSelection) {
      return false; // Only regular sizes selected
    }
    return false;
  }

  return false;
};

/**
 * Checks if the product should show a plus size image based on the user's profile
 * and selected URL filters if applicable for the product.
 * e.g. if the user selects only "size_top: 3XL" and the product is a pant,
 * then we should not show the plus size image
 *
 * @param {Object} param - The parameters for the function.
 * @param {Array} param.productFacets - The product facets.
 * @param {Object} param.selectedUrlFilters - The selected URL filters.
 * @param {Object} param.profileSizes - The user's profile sizes.
 * @param {string} param.gridType - The type of grid being used.
 * @returns {boolean} - True if the product should show a plus size image, false otherwise.
 */
export const shouldShowPlusSizeImageForProductCard = ({
  productFacets = [],
  selectedUrlFilters,
  profileSizes,
  gridType,
}: {
  productFacets: any[];
  selectedUrlFilters: Record<string, any>;
  profileSizes?: any;
  gridType?: string;
}): boolean => {
  // Savage does not have plus size images
  if (useSavagePlusSizeFallback) {
    return false;
  }

  // TODO: this is temp to match prod behavior.
  // We will standardize this logic for all grids in a later ticket:
  // https://techstyle-prod.atlassian.net/browse/FLCE-3225
  if (gridType === GRID_TYPES.PRODUCTS_V2_GRID) {
    // console.log('Using Products V2 logic for plus size image selection');
    return shouldShowPlusSizeImageForProductCardForProductsV2({
      selectedUrlFilters,
      profileSizes,
    });
  }

  const isPlusProfile = getIsPlusProfile(profileSizes);
  // User has a plus profile, so we assume the preference is to see plus size images
  if (isPlusProfile) {
    return true;
  }

  if (!productFacets?.length || !selectedUrlFilters) {
    return false;
  }

  // Check if the user has selected any plus size filter options
  // and if those selections are relevant to the product type
  // e.g. if the user selects only "size_top: 3XL" and the product is a pant,
  // then we should not show the plus size image
  const { sizeFacets } = isolateSizeFacets(productFacets);
  const filteredSizeFacets = sizeFacets
    .filter((facet) => {
      if (facet.values?.length === 1 && facet.values[0] === 'no-size') {
        return false;
      }
      return true;
    })
    ?.map((facet) => cioFacetsToUrlFilters[facet.name] || facet.name);

  return filteredSizeFacets?.some((facet) => {
    const selectedFacet = selectedUrlFilters[facet];
    if (selectedFacet) {
      return selectedFacet.some((value) => plusSizes.has(value));
    }
    return false;
  });
};
